/**
 * Base class for all route builders.
 * Provides common functionality for building route paths.
 */
class BaseRouteBuilder {
  constructor(private readonly existingPath: string) {}

  /**
   * Builds and returns the complete route path.
   */
  build(): string {
    return '/' + this.existingPath;
  }

  buildRelative(): string {
    return this.existingPath;
  }

  /**
   * Helper method to append a path segment.
   */
  protected appendPath(segment: string): string {
    if (this.existingPath === '') {
      return `${segment}`;
    }
    return `${this.existingPath}/${segment}`;
  }
}

class RootRouteBuilder extends BaseRouteBuilder {
  signup(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('signup'));
  }

  imprint(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('imprint'));
  }

  privacy(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('privacy'));
  }

  termsOfUse(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('terms-of-use'));
  }

  interview(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('interview'));
  }

  app(): AppRouteBuilder {
    return new AppRouteBuilder(this.appendPath('app'));
  }

  login(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('login'));
  }

  error(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('error'));
  }
}

class AppRouteBuilder extends BaseRouteBuilder {
  startupDashboard(): StartupDashboardRouteBuilder {
    return new StartupDashboardRouteBuilder(this.appendPath('dashboard'));
  }

  maDashboard(): MaDashboardRouteBuilder {
    return new MaDashboardRouteBuilder(this.appendPath('ma-dashboard'));
  }

  hpDashboard(): HpDashboardRouteBuilder {
    return new HpDashboardRouteBuilder(this.appendPath('hp-dashboard'));
  }
}

class MaDashboardRouteBuilder extends BaseRouteBuilder {
  welcome(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('welcome'));
  }

  cycles(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('cycles'));
  }

  handelspartner(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('handelspartner'));
  }

  products(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('products'));
  }

  voting(cycleId?: string): BaseRouteBuilder {
    if (cycleId) {
      return new BaseRouteBuilder(this.appendPath(`voting/${cycleId}`));
    }
    return new BaseRouteBuilder(this.appendPath('voting'));
  }

  mailjetExport(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('mailjet-export'));
  }

  registrationDebugger(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('registration-debugger'));
  }

  productEdit(productId: string): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath(`product/edit/${productId}`));
  }

  productView(productId: string, cycleId?: string): BaseRouteBuilder {
    if (cycleId) {
      return new BaseRouteBuilder(this.appendPath(`product/${productId}/cycle/${cycleId}`));
    }
    return new BaseRouteBuilder(this.appendPath(`product/${productId}`));
  }

  users(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('users'));
  }

  editUser(mnetId: string): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath(`users/${mnetId}`));
  }
}

class HpDashboardRouteBuilder extends BaseRouteBuilder {
  welcome(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('welcome'));
  }

  voting(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('voting'));
  }

  products(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('products'));
  }

  productView(productId: string): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath(`product/${productId}`));
  }
}

class StartupDashboardRouteBuilder extends BaseRouteBuilder {
  welcome(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('welcome'));
  }

  product(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('product'));
  }

  profile(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('profile'));
  }

  preview(): BaseRouteBuilder {
    return new BaseRouteBuilder(this.appendPath('preview'));
  }
}

/**
 * Main entry point for building routes.
 *
 * Usage examples:
 * - RouteBuilder.build() => '/'
 * - RouteBuilder.signup().build() => '/signup'
 * - RouteBuilder.app().maDashboard().productView('123').build() => '/app/ma-dashboard/product/123'
 */
export const RouteBuilder = new RootRouteBuilder('');

export const MaDashboardRelativeRouteBuilder = new MaDashboardRouteBuilder('');
export const HpDashboardRelativeRouteBuilder = new HpDashboardRouteBuilder('');
export const StartupDashboardRelativeRouteBuilder = new StartupDashboardRouteBuilder('');
export const AppRelativeRouteBuilder = new AppRouteBuilder('');
